import { useEffect, useMemo, useState, type FormEvent } from 'react'
import { useSearchParams } from 'react-router-dom'
import { GrundartikelLink } from '@/components/catalog/GrundartikelLink'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { ExternalLink } from 'lucide-react'
import { Panel } from '@/components/Panel'
import { Button } from '@/components/ui/button'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog'
import { ApiError, isTransportError } from '@/api/client'
import type { PricingStrategy } from '@/api/pricing'
import {
  applyOfferStrategy, getEbayOffer, listOfferBatches, OFFER_BATCH_MAX, offerFehlerText, searchOffers, undoOfferBatch,
  type BatchListEintrag, type BatchResult, type EbayOffer, type OfferTreffer, type OfferVariante, type UndoResult,
} from '@/api/offerStrategy'
import { neueUuid } from '@/lib/uuid'
import { cn } from '@/lib/utils'
import { nachStrategieAenderung } from '@/pages/grundartikel/tabs/rollout/strategieStand'
import { ArtikelKennung, VersandChip } from '@/components/rollout/RolloutAnzeige'

/**
 * Reiter „Angebote“ (Plan 2026-10-19 Strategie je Angebot): Strategie-Auswahl für alle oder
 * gewählte Child-Artikel eines eBay-Angebots in einem Zug. Nur die Strategie — kein Rollout-Start,
 * keine Preisänderung, nichts am Portal. Der Server prüft Stand (Token), Zugehörigkeit und
 * Wiederholung (request_id) unter Sperre.
 */

const STRATEGIE_NAME: Record<PricingStrategy, string> = { margin: 'Marge', break_even: 'Break-Even' }

const strategieText = (s: { amazon: PricingStrategy; base: PricingStrategy }) =>
  s.amazon === s.base ? STRATEGIE_NAME[s.amazon] : `Amazon ${STRATEGIE_NAME[s.amazon]} · Basis ${STRATEGIE_NAME[s.base]}`

const fmtZeit = (iso: string) => new Date(iso).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' })

const fehlerText = (err: unknown) =>
  err instanceof ApiError ? offerFehlerText(err.message) : err instanceof Error ? err.message : 'Unbekannter Fehler'

const auswahlKlasse = 'h-8 rounded-md border border-line bg-bg px-2 text-xs text-ink focus-visible:outline-2 focus-visible:outline-primary'

const variantenText = (n: number) => `${n} ${n === 1 ? 'Variante' : 'Varianten'}`

/** Vorschau-Momentaufnahme: genau das, was der Bediener gesehen hat, wird gesendet. */
interface Vorschau {
  itemId: string
  ids: number[]
  strategy: PricingStrategy
  token: string
  requestId: string
  aendern: OfferVariante[]
  bleiben: number
}

function baueVorschau(offer: EbayOffer, ids: number[], strategy: PricingStrategy): Vorschau {
  const gewaehlt = offer.varianten.filter((v) => ids.includes(v.stock_item_id))
  const aendern = gewaehlt.filter((v) => v.strategie.amazon !== strategy || v.strategie.base !== strategy)
  return {
    itemId: offer.item_id,
    ids: gewaehlt.map((v) => v.stock_item_id),
    strategy,
    token: offer.preview_token,
    requestId: neueUuid(),
    aendern,
    bleiben: gewaehlt.length - aendern.length,
  }
}

function StrategieZelle({ v }: { v: OfferVariante }) {
  if (!v.strategie.uneinheitlich) return <span className="text-ink">{STRATEGIE_NAME[v.strategie.amazon]}</span>
  return (
    <span
      title={strategieText(v.strategie)}
      className="rounded bg-warn-soft px-1.5 py-px ring-1 ring-warn/60 text-[10px] font-semibold text-ink"
    >
      uneinheitlich
    </span>
  )
}

// ─── Suche ────────────────────────────────────────────────────────────────

function SuchPanel({ onOeffnen }: { onOeffnen: (itemId: string) => void }) {
  const [q, setQ] = useState('')
  const suche = useMutation({
    mutationFn: (text: string) => searchOffers(text),
    onSuccess: (treffer) => {
      const einziger = treffer.length === 1 ? treffer[0] : undefined
      if (einziger) onOeffnen(einziger.item_id)
    },
  })
  const absenden = (e: FormEvent) => {
    e.preventDefault()
    const text = q.trim()
    if (text) suche.mutate(text)
  }
  const treffer: OfferTreffer[] = suche.data ?? []
  return (
    <Panel title="Angebot suchen" subtitle="eBay-Link, Angebotsnummer (ItemID) oder Titel – nur aktive eBay-Angebote.">
      <form onSubmit={absenden} className="flex flex-wrap gap-2" role="search">
        <input
          type="search"
          aria-label="Angebot suchen"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="https://www.ebay.de/itm/… oder 112503418873 oder Titel"
          className="h-9 min-w-0 flex-1 rounded-md border border-line bg-bg px-3 text-sm text-ink placeholder:text-muted focus-visible:outline-2 focus-visible:outline-primary"
        />
        <Button type="submit" disabled={suche.isPending || q.trim() === ''}>Suchen</Button>
      </form>
      {suche.isError && (
        <p role="alert" className="mt-3 rounded-md border border-coral bg-coral-soft px-3 py-2 text-sm text-ink">{fehlerText(suche.error)}</p>
      )}
      {suche.isSuccess && treffer.length === 0 && (
        <p className="mt-3 text-sm text-ink-2">Kein aktives eBay-Angebot gefunden. Link oder Nummer prüfen.</p>
      )}
      {treffer.length > 1 && (
        <ul className="mt-3 divide-y divide-line-2 rounded-md border border-line" aria-label="Treffer">
          {treffer.map((t) => (
            <li key={t.item_id}>
              <button
                type="button"
                onClick={() => onOeffnen(t.item_id)}
                className="flex w-full flex-wrap items-baseline gap-x-3 px-3 py-2 text-left text-sm hover:bg-panel-2 focus-visible:outline-2 focus-visible:outline-primary"
              >
                <span className="min-w-0 flex-1 text-ink">{t.title ?? '(ohne Titel)'}</span>
                <span className="font-mono text-xs text-ink-2">{t.item_id}</span>
                <span className="text-xs text-muted">{t.account_key} · {variantenText(t.varianten)}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  )
}

// ─── Vorschau-Dialog ──────────────────────────────────────────────────────

interface VorschauProps {
  vorschau: Vorschau | null
  aktuellerToken: string | null
  busy: boolean
  fehler: string | null
  /** Antwort ging verloren: gleicher request_id, Bestätigen heißt „Erneut senden“. */
  wiederholen: boolean
  onUebernehmen: () => void
  onAbbrechen: () => void
  onBestaetigen: () => void
}

function VorschauDialog({ vorschau, aktuellerToken, busy, fehler, wiederholen, onUebernehmen, onAbbrechen, onBestaetigen }: VorschauProps) {
  const veraltet = vorschau !== null && aktuellerToken !== null && aktuellerToken !== vorschau.token && !wiederholen
  return (
    <Dialog open={vorschau !== null} onOpenChange={(o) => { if (!o && !busy) onAbbrechen() }}>
      <DialogContent className="sm:max-w-xl">
        {vorschau && (
          <>
            <DialogHeader>
              <DialogTitle className="font-display">Strategie → {STRATEGIE_NAME[vorschau.strategy]}</DialogTitle>
              <DialogDescription>
                {variantenText(vorschau.ids.length)} gewählt: {vorschau.aendern.length} ändern sich, {vorschau.bleiben} {vorschau.bleiben === 1 ? 'bleibt' : 'bleiben'}.
              </DialogDescription>
            </DialogHeader>
            {vorschau.aendern.length > 0 ? (
              <ol className="max-h-[45vh] overflow-y-auto rounded-md border border-line" aria-label="Änderungen">
                {vorschau.aendern.map((v) => (
                  <li key={v.stock_item_id} className="grid grid-cols-[minmax(0,1fr)_auto] items-baseline gap-x-4 border-b border-line-2 px-3 py-1.5 last:border-b-0">
                    <span className="min-w-0 truncate text-sm">
                      {v.kennung?.stern && <GrundartikelLink code={v.kennung.stern} className="mr-1.5 font-mono font-bold text-ink" />}
                      <span className={v.kennung?.stern ? 'font-mono text-xs text-muted' : 'font-mono text-ink'}>{v.sku}</span>
                      {v.variation_key && <span className="ml-2 text-xs text-ink-2">{v.variation_key}</span>}
                    </span>
                    <span className="whitespace-nowrap text-xs">
                      <span className="text-ink-2">{strategieText(v.strategie)}</span>
                      <span className="mx-1.5 text-muted">→</span>
                      <b className="text-ink">{STRATEGIE_NAME[vorschau.strategy]}</b>
                    </span>
                  </li>
                ))}
              </ol>
            ) : (
              <p className="rounded-md bg-panel-2 px-3 py-2 text-sm text-ink-2">Alle gewählten Varianten haben diese Strategie schon. Bestätigen ändert nichts.</p>
            )}
            <p className="rounded-md border-l-4 border-primary bg-primary-soft px-3 py-2 text-xs text-ink">
              Nur die Strategie-Auswahl (Amazon und Basis). Kein Rollout-Start, keine Preisänderung, nichts am Portal.
              Der Zielpreis wird beim nächsten Start neu berechnet.
            </p>
            {veraltet && (
              <div role="alert" className="flex flex-wrap items-center gap-2 rounded-md border border-coral bg-coral-soft px-3 py-2 text-sm text-ink">
                <span className="flex-1">Das Angebot hat einen neuen Stand. Bitte Vorschau mit dem neuen Stand prüfen.</span>
                <Button size="sm" variant="outline" onClick={onUebernehmen} disabled={busy}>Neuen Stand übernehmen</Button>
              </div>
            )}
            {fehler && <p role="alert" className="rounded-md border border-coral bg-coral-soft px-3 py-2 text-sm text-ink">{fehler}</p>}
            <DialogFooter>
              <Button variant="outline" onClick={onAbbrechen} disabled={busy}>Abbrechen</Button>
              <Button onClick={onBestaetigen} disabled={busy || veraltet || vorschau.ids.length === 0}>
                {wiederholen ? 'Erneut senden' : 'Strategie übernehmen'}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}

// ─── Rückgängig-Dialog ────────────────────────────────────────────────────

interface UndoZiel { batch_id: string; geaendert: number; titel: string; strategy: PricingStrategy }

function UndoDialog({ ziel, busy, fehler, onAbbrechen, onBestaetigen }: {
  ziel: UndoZiel | null; busy: boolean; fehler: string | null; onAbbrechen: () => void; onBestaetigen: () => void
}) {
  return (
    <Dialog open={ziel !== null} onOpenChange={(o) => { if (!o && !busy) onAbbrechen() }}>
      <DialogContent>
        {ziel && (
          <>
            <DialogHeader>
              <DialogTitle className="font-display">Änderung rückgängig machen</DialogTitle>
              <DialogDescription>
                {ziel.titel}: {variantenText(ziel.geaendert)} → {STRATEGIE_NAME[ziel.strategy]}.
              </DialogDescription>
            </DialogHeader>
            <p className="text-sm text-ink">
              Setzt diese Varianten auf die Strategie vor der Änderung zurück. Varianten, deren Strategie danach
              erneut geändert wurde, bleiben wie sie sind und werden als übersprungen gezählt.
            </p>
            <p className="text-xs text-ink-2">Kein Rollout-Start, keine Preisänderung, nichts am Portal.</p>
            {fehler && <p role="alert" className="rounded-md border border-coral bg-coral-soft px-3 py-2 text-sm text-ink">{fehler}</p>}
            <DialogFooter>
              <Button variant="outline" onClick={onAbbrechen} disabled={busy}>Abbrechen</Button>
              <Button onClick={onBestaetigen} disabled={busy}>Rückgängig machen</Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}

const undoText = (u: { wiederhergestellt: number; uebersprungen: number }) =>
  `${u.wiederhergestellt} wiederhergestellt · ${u.uebersprungen} übersprungen`

// ─── Angebot ──────────────────────────────────────────────────────────────

function AngebotPanel({ offer, onOeffnen, onErgebnis }: {
  offer: EbayOffer
  onOeffnen: (itemId: string) => void
  onErgebnis: (r: BatchResult) => void
}) {
  const queryClient = useQueryClient()
  const [auswahl, setAuswahl] = useState<Set<number>>(new Set())
  const [strategie, setStrategie] = useState<PricingStrategy | ''>('')
  const [vorschau, setVorschau] = useState<Vorschau | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)
  const [wiederholen, setWiederholen] = useState(false)

  // Auswahl gilt je Angebot und nur für aktuelle Mitglieder.
  useEffect(() => { setAuswahl(new Set()) }, [offer.item_id])
  const mitglieder = useMemo(() => new Set(offer.varianten.map((v) => v.stock_item_id)), [offer.varianten])
  const gewaehlt = useMemo(() => [...auswahl].filter((id) => mitglieder.has(id)).sort((a, b) => a - b), [auswahl, mitglieder])
  const alle = gewaehlt.length === offer.varianten.length && offer.varianten.length > 0

  const umschalten = (id: number) => setAuswahl((s) => {
    const n = new Set(s)
    if (n.has(id)) n.delete(id)
    else n.add(id)
    return n
  })

  const schreiben = useMutation({
    mutationFn: (v: Vorschau) => applyOfferStrategy(v.itemId, {
      stock_item_ids: v.ids, strategy: v.strategy, preview_token: v.token, request_id: v.requestId,
    }),
    onSuccess: (res) => {
      setVorschau(null)
      setFehler(null)
      setWiederholen(false)
      setAuswahl(new Set())
      onErgebnis(res)
      nachStrategieAenderung(queryClient)
      void queryClient.invalidateQueries({ queryKey: ['offer-ebay'] })
      void queryClient.invalidateQueries({ queryKey: ['offer-batches'] })
    },
    onError: (err) => {
      if (isTransportError(err)) {
        // Ausgang unklar – der Batch kann gespeichert sein: Starts sperren und neu laden wie nach Erfolg.
        nachStrategieAenderung(queryClient)
        void queryClient.invalidateQueries({ queryKey: ['offer-ebay'] })
        void queryClient.invalidateQueries({ queryKey: ['offer-batches'] })
        setWiederholen(true)
        setFehler('Antwort nicht erhalten – unklar, ob gespeichert. „Erneut senden“ nutzt dieselbe Kennung; doppelt geschrieben wird nichts.')
        return
      }
      setWiederholen(false)
      setFehler(fehlerText(err))
      if (err instanceof ApiError && err.status === 409) {
        void queryClient.invalidateQueries({ queryKey: ['offer-ebay', offer.item_id] })
      }
    },
  })

  const oeffneVorschau = () => {
    if (!strategie) return
    setFehler(null)
    setWiederholen(false)
    setVorschau(baueVorschau(offer, gewaehlt, strategie))
  }
  const uebernehmen = () => {
    if (!vorschau) return
    setFehler(null)
    setVorschau(baueVorschau(offer, vorschau.ids, vorschau.strategy))
  }

  const zuViele = gewaehlt.length > OFFER_BATCH_MAX
  return (
    <Panel
      title={offer.title ?? '(ohne Titel)'}
      subtitle={`eBay ${offer.item_id} · ${offer.account_key} · ${variantenText(offer.varianten.length)}`}
      actions={(
        <a
          href={offer.url} target="_blank" rel="noreferrer"
          className="inline-flex items-center gap-1 text-xs text-ink-2 hover:text-ink focus-visible:outline-2 focus-visible:outline-primary"
        >
          Bei eBay ansehen <ExternalLink className="size-3" aria-hidden />
        </a>
      )}
      bodyClassName="p-0"
    >
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 border-b border-line-2 px-5 py-3">
        <span className="text-xs text-ink-2" aria-live="polite">{gewaehlt.length} von {offer.varianten.length} gewählt</span>
        <label className="ml-auto flex items-center gap-1.5 text-xs text-ink-2">
          <span aria-hidden>Strategie</span>
          <select
            aria-label="Neue Strategie" className={auswahlKlasse} value={strategie}
            onChange={(e) => setStrategie(e.target.value as PricingStrategy | '')}
          >
            <option value="">wählen…</option>
            <option value="margin">Marge</option>
            <option value="break_even">Break-Even</option>
          </select>
        </label>
        <Button size="sm" onClick={oeffneVorschau} disabled={gewaehlt.length === 0 || !strategie || zuViele}>Vorschau</Button>
        {zuViele && <span className="w-full text-xs text-coral">Höchstens {OFFER_BATCH_MAX} Varianten je Änderung.</span>}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm" aria-label={`Varianten des Angebots ${offer.item_id}`}>
          <thead>
            <tr className="border-b border-line-2 text-left text-[11px] uppercase tracking-wider text-muted">
              <th className="w-10 px-5 py-2">
                <input
                  type="checkbox" aria-label="Alle Varianten wählen" checked={alle}
                  ref={(el) => { if (el) el.indeterminate = gewaehlt.length > 0 && !alle }}
                  onChange={() => setAuswahl(alle ? new Set() : new Set(mitglieder))}
                  className="size-4 accent-primary"
                />
              </th>
              <th className="py-2 pr-4 font-medium">Variante</th>
              <th className="py-2 pr-4 font-medium">Grundartikel</th>
              <th className="py-2 pr-4 font-medium">Strategie</th>
              <th className="py-2 pr-5 font-medium">Auch in</th>
            </tr>
          </thead>
          <tbody>
            {offer.varianten.map((v) => {
              const an = auswahl.has(v.stock_item_id)
              return (
                <tr key={v.stock_item_id} className={cn('border-b border-line-2 last:border-b-0', an && 'bg-primary-soft/40')}>
                  <td className="px-5 py-2 align-top">
                    <input
                      type="checkbox" aria-label={`Variante ${v.sku} wählen`} checked={an}
                      onChange={() => umschalten(v.stock_item_id)} className="size-4 accent-primary"
                    />
                  </td>
                  <td className="py-2 pr-4 align-top">
                    <ArtikelKennung kennung={v.kennung} sku={v.sku} name={v.variation_key ?? v.name} mitGrundartikel={false} />
                    {v.versand && <div className="mt-1"><VersandChip versand={v.versand} /></div>}
                  </td>
                  <td className="py-2 pr-4 align-top">
                    {v.grundartikel ? (
                      <GrundartikelLink
                        code={v.kennung?.grundartikel?.stern ?? v.grundartikel.sku}
                        className="font-mono text-xs text-ink underline-offset-2 hover:underline focus-visible:outline-2 focus-visible:outline-primary"
                      >
                        {v.kennung?.grundartikel?.stern
                          ? <><b className="text-[13px]">{v.kennung.grundartikel.stern}</b> <span className="text-muted">{v.grundartikel.sku}</span></>
                          : v.grundartikel.sku}
                      </GrundartikelLink>
                    ) : <span className="text-xs text-muted">—</span>}
                  </td>
                  <td className="py-2 pr-4 align-top text-xs"><StrategieZelle v={v} /></td>
                  <td className="py-2 pr-5 align-top">
                    <div className="flex flex-wrap gap-1">
                      {v.auch_in.map((a) => (
                        <button
                          key={a.item_id} type="button" onClick={() => onOeffnen(a.item_id)} title={a.title ?? undefined}
                          className="rounded border border-line bg-panel-2 px-1.5 py-px font-mono text-[11px] text-ink-2 hover:text-ink focus-visible:outline-2 focus-visible:outline-primary"
                        >
                          {a.item_id}
                        </button>
                      ))}
                    </div>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
      <VorschauDialog
        vorschau={vorschau}
        aktuellerToken={offer.preview_token}
        busy={schreiben.isPending}
        fehler={fehler}
        wiederholen={wiederholen}
        onUebernehmen={uebernehmen}
        onAbbrechen={() => { setVorschau(null); setFehler(null); setWiederholen(false) }}
        onBestaetigen={() => { if (vorschau) schreiben.mutate(vorschau) }}
      />
    </Panel>
  )
}

// ─── Letzte Änderungen ────────────────────────────────────────────────────

function BatchListe({ batches, onOeffnen, onUndo }: {
  batches: BatchListEintrag[]
  onOeffnen: (itemId: string) => void
  onUndo: (z: UndoZiel) => void
}) {
  if (batches.length === 0) return <p className="px-5 py-4 text-sm text-ink-2">Noch keine Änderung über diesen Reiter.</p>
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm" aria-label="Letzte Strategie-Änderungen">
        <thead>
          <tr className="border-b border-line-2 text-left text-[11px] uppercase tracking-wider text-muted">
            <th className="px-5 py-2 font-medium">Zeit</th>
            <th className="py-2 pr-4 font-medium">Angebot</th>
            <th className="py-2 pr-4 font-medium">Strategie</th>
            <th className="py-2 pr-4 font-medium">Geändert</th>
            <th className="py-2 pr-4 font-medium">Von</th>
            <th className="py-2 pr-5 font-medium">Status</th>
          </tr>
        </thead>
        <tbody>
          {batches.map((b) => (
            <tr key={b.batch_id} className="border-b border-line-2 last:border-b-0">
              <td className="whitespace-nowrap px-5 py-2 text-xs text-ink-2">{fmtZeit(b.created_at)}</td>
              <td className="max-w-[320px] py-2 pr-4">
                <button
                  type="button" onClick={() => onOeffnen(b.item_id)}
                  className="block max-w-full truncate text-left text-ink hover:underline focus-visible:outline-2 focus-visible:outline-primary"
                >
                  {b.title ?? b.item_id}
                </button>
                <span className="font-mono text-[11px] text-muted">{b.item_id}</span>
              </td>
              <td className="py-2 pr-4 text-xs">{STRATEGIE_NAME[b.strategy]}</td>
              <td className="whitespace-nowrap py-2 pr-4 text-xs">
                <b className="text-ink">{b.geaendert}</b> <span className="text-ink-2">von {b.geaendert + b.unveraendert}</span>
              </td>
              <td className="py-2 pr-4 text-xs text-ink-2">{b.actor_name ?? '—'}</td>
              <td className="py-2 pr-5 text-xs">
                {b.rueckgaengig ? (
                  <span className="text-ink-2" title={`am ${fmtZeit(b.rueckgaengig.am)}`}>
                    Rückgängig gemacht · {undoText(b.rueckgaengig)}
                  </span>
                ) : b.geaendert === 0 ? (
                  <span className="text-muted">keine Änderung</span>
                ) : (
                  <Button
                    size="sm" variant="outline"
                    onClick={() => onUndo({ batch_id: b.batch_id, geaendert: b.geaendert, titel: b.title ?? b.item_id, strategy: b.strategy })}
                  >
                    Rückgängig
                  </Button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// ─── Reiter ───────────────────────────────────────────────────────────────

export function AngeboteTab() {
  const queryClient = useQueryClient()
  const [params, setParams] = useSearchParams()
  const itemId = params.get('angebot')
  const [ergebnis, setErgebnis] = useState<{ batch: BatchResult; titel: string; undo: UndoResult | null } | null>(null)
  const [undoZiel, setUndoZiel] = useState<UndoZiel | null>(null)
  const [undoFehler, setUndoFehler] = useState<string | null>(null)

  const oeffnen = (id: string) => {
    const p = new URLSearchParams(params)
    p.set('tab', 'angebote')
    p.set('angebot', id)
    setParams(p, { replace: true })
  }

  const offerQ = useQuery({
    queryKey: ['offer-ebay', itemId],
    queryFn: () => getEbayOffer(itemId!),
    enabled: itemId !== null,
  })
  const batchesQ = useQuery({ queryKey: ['offer-batches'], queryFn: listOfferBatches })

  const undo = useMutation({
    mutationFn: (batchId: string) => undoOfferBatch(batchId),
    onSuccess: (res) => {
      setUndoZiel(null)
      setUndoFehler(null)
      setErgebnis((e) => (e && e.batch.batch_id === res.batch_id ? { ...e, undo: res } : e))
      toast.success(`Rückgängig gemacht: ${undoText({ wiederhergestellt: res.wiederhergestellt.length, uebersprungen: res.uebersprungen.length })}`)
      nachStrategieAenderung(queryClient)
      void queryClient.invalidateQueries({ queryKey: ['offer-ebay'] })
      void queryClient.invalidateQueries({ queryKey: ['offer-batches'] })
    },
    onError: (err) => {
      const unklar = isTransportError(err)
      setUndoFehler(unklar
        ? 'Antwort nicht erhalten – unklar, ob rückgängig gemacht. Liste neu geladen; bitte Status prüfen.'
        : fehlerText(err))
      if (unklar) {
        // Rückgängig kann gespeichert sein: Starts sperren und Angebot neu laden.
        nachStrategieAenderung(queryClient)
        void queryClient.invalidateQueries({ queryKey: ['offer-ebay'] })
      }
      void queryClient.invalidateQueries({ queryKey: ['offer-batches'] })
    },
  })

  return (
    <div className="space-y-4">
      <SuchPanel onOeffnen={oeffnen} />

      {ergebnis && (
        <div role="status" className="flex flex-wrap items-center gap-3 rounded-md border-l-4 border-success bg-success-soft px-4 py-2.5 text-sm text-ink">
          <span className="flex-1">
            {ergebnis.batch.geaendert === 0
              ? `Keine Änderung nötig – alle gewählten Varianten hatten schon ${STRATEGIE_NAME[ergebnis.batch.strategy]}.`
              : `Strategie für ${variantenText(ergebnis.batch.geaendert)} übernommen (${STRATEGIE_NAME[ergebnis.batch.strategy]}).`}
            {ergebnis.batch.wiederholt && ' War bereits gespeichert.'}
            {ergebnis.undo && (
              <b className="ml-1">
                Rückgängig gemacht: {undoText({ wiederhergestellt: ergebnis.undo.wiederhergestellt.length, uebersprungen: ergebnis.undo.uebersprungen.length })}.
              </b>
            )}
          </span>
          {ergebnis.batch.geaendert > 0 && !ergebnis.undo && (
            <Button
              size="sm" variant="outline"
              onClick={() => {
                setUndoFehler(null)
                setUndoZiel({ batch_id: ergebnis.batch.batch_id, geaendert: ergebnis.batch.geaendert, titel: ergebnis.titel, strategy: ergebnis.batch.strategy })
              }}
            >
              Rückgängig
            </Button>
          )}
          <button type="button" onClick={() => setErgebnis(null)} className="text-xs text-ink-2 hover:text-ink">Ausblenden</button>
        </div>
      )}

      {itemId !== null && offerQ.isPending && <p className="text-sm text-ink-2">Angebot wird geladen…</p>}
      {itemId !== null && offerQ.isError && (
        <p role="alert" className="rounded-md border border-coral bg-coral-soft px-3 py-2 text-sm text-ink">
          {offerQ.error instanceof ApiError && offerQ.error.status === 404
            ? `Kein aktives eBay-Angebot ${itemId} mit Varianten in der Plattform.`
            : `Angebot konnte nicht geladen werden: ${fehlerText(offerQ.error)}`}
        </p>
      )}
      {offerQ.data && (
        <AngebotPanel
          offer={offerQ.data}
          onOeffnen={oeffnen}
          onErgebnis={(batch) => setErgebnis({ batch, titel: offerQ.data.title ?? offerQ.data.item_id, undo: null })}
        />
      )}

      <Panel title="Letzte Änderungen" subtitle="Strategie-Änderungen über diesen Reiter, neueste zuerst." bodyClassName="p-0">
        {batchesQ.isPending && <p className="px-5 py-4 text-sm text-ink-2">Wird geladen…</p>}
        {batchesQ.isError && <p role="alert" className="px-5 py-4 text-sm text-coral">Liste konnte nicht geladen werden.</p>}
        {batchesQ.data && (
          <BatchListe
            batches={batchesQ.data}
            onOeffnen={oeffnen}
            onUndo={(z) => { setUndoFehler(null); setUndoZiel(z) }}
          />
        )}
      </Panel>

      <UndoDialog
        ziel={undoZiel}
        busy={undo.isPending}
        fehler={undoFehler}
        onAbbrechen={() => { setUndoZiel(null); setUndoFehler(null) }}
        onBestaetigen={() => { if (undoZiel) undo.mutate(undoZiel.batch_id) }}
      />
    </div>
  )
}
